/**
 * The race of the current room as the client sees it.
 *
 * A race becomes "current" only when both halves have arrived: `race.start` (levels and the
 * start time) and a room snapshot naming the same race (its progress tells where a player
 * who reconnects or reloads the page resumes). The server may send them in either order.
 *
 * When `race.start` is repeated for the race that is already current (after a reconnect),
 * nothing changes: the engine keeps running the race locally as if nothing happened.
 */
import {
  DEFAULT_RACE_SETTINGS,
  LEVELS_PER_RACE,
  type LevelData,
  type PlayerId,
  type RaceInfo,
  type RaceProgressEntry,
  type RaceResultEntry,
  type RaceSettings,
  type RoomSnapshot,
  type ServerMessage,
} from '@cube-rush/shared';
import { batch, type ReadonlySignal, type Signal, signal } from '@preact/signals';
import type { ActiveRace } from '../game/contract';

/**
 * racer: takes part and has not given up or finished yet when the race was (re)opened;
 * spectator: joined mid-race, gave up, or had already finished before a page reload.
 */
export type RaceRole = 'racer' | 'spectator';

export interface RaceSession {
  race: ActiveRace;
  role: RaceRole;
}

export interface RaceResults {
  raceId: string;
  results: readonly RaceResultEntry[];
  /** Levels of that race: by levels, what a finisher completed (3 to 20, the room decides). */
  levelCount: number;
  /** What the race was: by levels or against the clock, its length and difficulty. */
  settings: RaceSettings;
}

type RaceStartMessage = Extract<ServerMessage, { t: 'race.start' }>;
type RaceProgressMessage = Extract<ServerMessage, { t: 'race.progress' }>;
type RaceEndMessage = Extract<ServerMessage, { t: 'race.end' }>;

interface PendingStart {
  raceId: string;
  startAt: number;
  endAt: number | null;
  participants: readonly PlayerId[];
  levels: readonly LevelData[];
}

const NO_PROGRESS: readonly RaceProgressEntry[] = [];

export class RaceStore {
  private readonly currentSignal: Signal<RaceSession | null> = signal(null);
  private readonly progressSignal: Signal<readonly RaceProgressEntry[]> = signal(NO_PROGRESS);
  private readonly resultsSignal: Signal<RaceResults | null> = signal(null);
  private readonly levelCountSignal: Signal<number> = signal(LEVELS_PER_RACE);

  /** The race the game screen shows, or null when no race runs. */
  readonly current: ReadonlySignal<RaceSession | null> = this.currentSignal;
  /** Progress of every participant of the room's current race. */
  readonly progress: ReadonlySignal<readonly RaceProgressEntry[]> = this.progressSignal;
  /** Results of the last finished race (kept until the next race starts or the room changes). */
  readonly results: ReadonlySignal<RaceResults | null> = this.resultsSignal;
  /** Levels of the room's latest race (running or finished), for "level 3/5" in the room. */
  readonly levelCount: ReadonlySignal<number> = this.levelCountSignal;

  private pendingStart: PendingStart | null = null;
  private roomRace: RaceInfo | null = null;
  /** The latest race whose `race.start` arrived: how many levels it has, what it is. */
  private startedRace: { raceId: string; levels: number; settings: RaceSettings } | null = null;

  constructor(private readonly myId: () => PlayerId | null) {}

  handleRaceStart(message: RaceStartMessage): void {
    const { raceId, startAt, endAt, participants, levels, settings } = message;
    this.pendingStart = { raceId, startAt, endAt, participants, levels };
    this.startedRace = { raceId, levels: levels.length, settings };
    this.levelCountSignal.value = levels.length;
    this.tryActivate();
  }

  handleRoom(room: RoomSnapshot): void {
    const info = room.race;
    if (info === null || room.phase === 'lobby') {
      // No race (any more): the results phase is over or the race was cancelled.
      this.pendingStart = null;
      this.roomRace = null;
      batch(() => {
        this.currentSignal.value = null;
        this.progressSignal.value = NO_PROGRESS;
      });
      return;
    }
    this.roomRace = info;
    if (this.pendingStart && this.pendingStart.raceId !== info.raceId) this.pendingStart = null;
    batch(() => {
      if (this.currentSignal.peek()?.race.raceId !== info.raceId) this.currentSignal.value = null;
      this.progressSignal.value = info.progress;
      this.levelCountSignal.value = info.levelCount;
      if (room.phase === 'results') {
        this.pendingStart = null;
        this.currentSignal.value = null;
        if (room.results) this.setResults(info.raceId, room.results);
      }
    });
    if (room.phase !== 'results') this.tryActivate();
  }

  handleProgress(message: RaceProgressMessage): void {
    if (message.raceId === this.roomRace?.raceId) this.progressSignal.value = message.progress;
  }

  handleRaceEnd(message: RaceEndMessage): void {
    this.pendingStart = null;
    batch(() => {
      this.currentSignal.value = null;
      this.setResults(message.raceId, message.results);
    });
  }

  /** The local player gave up: the race stays current, but only to be watched. */
  markQuit(): void {
    const current = this.currentSignal.peek();
    if (current && current.role === 'racer') {
      this.currentSignal.value = { ...current, role: 'spectator' };
    }
  }

  /** Forgets everything (left the room, logged out). */
  clear(): void {
    this.pendingStart = null;
    this.roomRace = null;
    this.startedRace = null;
    batch(() => {
      this.currentSignal.value = null;
      this.progressSignal.value = NO_PROGRESS;
      this.resultsSignal.value = null;
      this.levelCountSignal.value = LEVELS_PER_RACE;
    });
  }

  private setResults(raceId: string, results: readonly RaceResultEntry[]): void {
    // The room's snapshot names the race; whoever saw it start also has its race.start.
    const info = this.roomRace?.raceId === raceId ? this.roomRace : null;
    const started = this.startedRace?.raceId === raceId ? this.startedRace : null;
    const levelCount =
      info?.levelCount ??
      started?.levels ??
      results.find((entry) => entry.place !== null)?.levels ??
      LEVELS_PER_RACE;
    const settings = info?.settings ?? started?.settings ?? DEFAULT_RACE_SETTINGS;
    batch(() => {
      this.resultsSignal.value = { raceId, results, levelCount, settings };
      this.levelCountSignal.value = levelCount;
    });
  }

  private tryActivate(): void {
    const start = this.pendingStart;
    const info = this.roomRace;
    if (!start || !info || start.raceId !== info.raceId) return;
    if (this.currentSignal.peek()?.race.raceId === start.raceId) return;

    const myId = this.myId();
    const entry = info.progress.find((p) => p.id === myId);
    const isParticipant = myId !== null && start.participants.includes(myId);
    const finished =
      entry !== undefined && (entry.finishTm !== null || entry.lvl >= start.levels.length);
    const lastLevel = Math.max(0, start.levels.length - 1);
    batch(() => {
      this.resultsSignal.value = null;
      this.currentSignal.value = {
        race: {
          raceId: start.raceId,
          levels: start.levels,
          startAt: start.startAt,
          endAt: start.endAt,
          participants: start.participants,
          resumeLevel: Math.min(Math.max(0, entry?.lvl ?? 0), lastLevel),
        },
        role: isParticipant && !entry?.quit && !finished ? 'racer' : 'spectator',
      };
    });
  }
}
